import { useCallback, useLayoutEffect, useMemo } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import type { SmartGuideLine } from '@/lib/guides/smart-guides'
import {
  objectBoundsOfShapeAtPosition,
  unionObjectBounds,
} from '@/lib/shapes/shape-canvas-object-bounds'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SnapRefsApi } from './use-snap-refs'

export function useSmartGuides(ctx: ShapeManagerStateApi & SnapRefsApi) {
  const { onActiveSmartGuidesChangeRef, selectedShapeIds, shapesRef } = ctx

  const getShapeCanvasBoundsAtPosition = useCallback(
    (shape: ShapeData, position?: { x: number; y: number }): ObjectBounds => {
      return objectBoundsOfShapeAtPosition(shape, position)
    },
    []
  )

  const getMovedSelectionBounds = useCallback(
    (
      movingShapes: Array<{ shape: ShapeData; initialX: number; initialY: number }>,
      rawDx: number,
      rawDy: number
    ): ObjectBounds | null => {
      const movedBounds = movingShapes.map(({ shape, initialX, initialY }) =>
        getShapeCanvasBoundsAtPosition(shape, {
          x: initialX + rawDx,
          y: initialY + rawDy,
        })
      )

      return unionObjectBounds(movedBounds)
    },
    [getShapeCanvasBoundsAtPosition]
  )

  const getOtherShapeBounds = useCallback(
    (excludeIds: Set<string>): ObjectBounds[] => {
      const result: ObjectBounds[] = []

      for (const shape of shapesRef.current) {
        if (excludeIds.has(shape.id) || shape.hidden) continue

        result.push(getShapeCanvasBoundsAtPosition(shape))
      }

      return result
    },
    [getShapeCanvasBoundsAtPosition, shapesRef]
  )

  const emitSmartGuides = useCallback(
    (guides: SmartGuideLine[]) => {
      onActiveSmartGuidesChangeRef.current?.(guides)
    },
    [onActiveSmartGuidesChangeRef]
  )

  const shapeSelectionKeyForSmartGuides = useMemo(
    () => [...selectedShapeIds].sort().join('|'),
    [selectedShapeIds]
  )

  // Object-panel rotation preview drives `activeSmartGuides`; clear when selection changes so
  // deselect (e.g. click canvas while the slider still shows a snap angle) does not leave a stale guide.
  useLayoutEffect(() => {
    emitSmartGuides([])
  }, [emitSmartGuides, shapeSelectionKeyForSmartGuides])

  return {
    getShapeCanvasBoundsAtPosition,
    getMovedSelectionBounds,
    getOtherShapeBounds,
    emitSmartGuides,
    shapeSelectionKeyForSmartGuides,
  }
}

export type SmartGuidesApi = ReturnType<typeof useSmartGuides>
